import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, FileText, Mail, Calendar as CalIcon, Brain, ArrowLeft, AlertTriangle, CheckCircle2, XCircle, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["new", "screened", "interview", "offer", "hired", "rejected"];

export default function CandidateDetail() {
  const { candId } = useParams();
  const [cand, setCand] = useState(null);
  const [job, setJob] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [generatingDecision, setGeneratingDecision] = useState(false);
  const [message, setMessage] = useState(null);
  const [genMsgOpen, setGenMsgOpen] = useState(false);
  const [msgStage, setMsgStage] = useState("interview_invite");
  const [actionOpen, setActionOpen] = useState(false);
  const [actionForm, setActionForm] = useState({ type: "relance", title: "", notes: "", due_at: "" });
  const [pmOpen, setPmOpen] = useState(false);
  const [pmForm, setPmForm] = useState({ outcome: "hired_success", notes: "", months_in_role: "" });

  const [matching, setMatching] = useState(false);
  const [jobMatches, setJobMatches] = useState(null);
  const [professionSuggestions, setProfessionSuggestions] = useState(null);
  const [creatingJob, setCreatingJob] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/candidates/${candId}`);
    setCand(data);
    if (data.ai?.job_matches) setJobMatches(data.ai.job_matches);
    if (data.ai?.profession_suggestions) setProfessionSuggestions(data.ai.profession_suggestions);
    if (data.job_id) {
      try {
        const { data: j } = await api.get(`/jobs/${data.job_id}`);
        setJob(j);
      } catch {
        setJob(null);
      }
    } else {
      setJob(null);
    }
  };
  useEffect(() => { load(); }, [candId]);

  const findBestJobs = async () => {
    // On appelle en priorité la bibliothèque des 62 métiers standards
    setMatching(true);
    try {
      const { data } = await api.post(`/candidates/${candId}/suggest-professions`);
      setProfessionSuggestions(data);
      toast.success(`${data.suggestions?.length || 0} métier(s) suggéré(s)`);
      // En bonus, on tente aussi de matcher les postes créés
      try {
        const { data: matches } = await api.post(`/candidates/${candId}/match-jobs`);
        setJobMatches(matches);
      } catch { /* pas de postes créés, on ignore */ }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur IA");
    } finally {
      setMatching(false);
    }
  };

  const suggestProfessions = async () => {
    setMatching(true);
    try {
      const { data } = await api.post(`/candidates/${candId}/suggest-professions`);
      setProfessionSuggestions(data);
      toast.success(`${data.suggestions?.length || 0} métier(s) suggéré(s) par l'IA`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur IA");
    } finally {
      setMatching(false);
    }
  };

  const createJobFromProfession = async (professionTitle) => {
    setCreatingJob(professionTitle);
    try {
      const fd = new FormData();
      fd.append("profession_title", professionTitle);
      await api.post(`/candidates/${candId}/create-job-from-profession`, fd);
      toast.success(`Poste "${professionTitle}" créé et assigné`);
      await load();
    } catch (e) {
      toast.error("Erreur création poste");
    } finally {
      setCreatingJob(null);
    }
  };

  const assignJob = async (jobId) => {
    const fd = new FormData();
    fd.append("job_id", jobId);
    try {
      await api.patch(`/candidates/${candId}/assign-job`, fd);
      toast.success("Candidat assigné à ce poste");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/candidates/${candId}/analyze`);
      setCand(data);
      toast.success("Analyse terminée");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur d'analyse IA");
    } finally { setAnalyzing(false); }
  };

  const genPlan = async () => {
    setGeneratingPlan(true);
    try {
      await api.post(`/candidates/${candId}/interview-plan`);
      await load();
      toast.success("Plan d'entretien généré");
    } catch (e) { toast.error("Erreur"); }
    finally { setGeneratingPlan(false); }
  };

  const genDecision = async () => {
    setGeneratingDecision(true);
    try {
      await api.post(`/candidates/${candId}/decision`);
      await load();
      toast.success("Décision générée");
    } catch (e) { toast.error("Erreur"); }
    finally { setGeneratingDecision(false); }
  };

  const genMessage = async () => {
    try {
      const { data } = await api.post("/candidates/draft-message", { candidate_id: candId, stage: msgStage });
      setMessage(data);
    } catch (e) { toast.error("Erreur"); }
  };

  const changeStage = async (stage) => {
    const fd = new FormData();
    fd.append("stage", stage);
    await api.patch(`/candidates/${candId}/stage`, fd);
    setCand({ ...cand, stage });
    toast.success("Stade mis à jour");
  };

  const createAction = async () => {
    if (!actionForm.title) return;
    await api.post("/actions", { ...actionForm, candidate_id: candId, due_at: actionForm.due_at || null });
    toast.success("Action créée");
    setActionOpen(false);
    setActionForm({ type: "relance", title: "", notes: "", due_at: "" });
  };

  const createPostMortem = async () => {
    if (!pmForm.notes) return;
    await api.post("/post-mortems", {
      candidate_id: candId,
      outcome: pmForm.outcome,
      notes: pmForm.notes,
      months_in_role: pmForm.months_in_role ? Number(pmForm.months_in_role) : null,
    });
    toast.success("Post-mortem enregistré");
    setPmOpen(false);
    setPmForm({ outcome: "hired_success", notes: "", months_in_role: "" });
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copié"); };

  if (!cand) return <div className="p-10 text-slate-500">Chargement…</div>;

  const ai = cand.ai || {};
  const just = ai.justification || {};
  const norm = ai.normalized || {};
  const triage = ai.triage || {};
  const barrier = ai.barrier || {};
  const plan = ai.interview_plan;
  const decision = ai.decision;
  const score = just.match_score;

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <Link to={cand.job_id ? `/app/jobs/${cand.job_id}` : "/app/talent"} className="text-sm text-slate-500 inline-flex items-center gap-1 mb-6 link-underline" data-testid="cand-back">
        <ArrowLeft className="w-3 h-3" /> {cand.job_id ? "Retour au poste" : "Retour au pool"}
      </Link>

      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-slate-500 mb-2">
            {job ? job.title : "Sans poste assigné · pool talents"}
          </div>
          <h1 className="font-display text-4xl tracking-tight" data-testid="cand-name">{cand.full_name}</h1>
          <div className="text-slate-500 mt-2 font-mono text-sm">{cand.email || "—"} · {cand.phone || "—"}</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={cand.stage} onValueChange={changeStage}>
            <SelectTrigger className="w-40" data-testid="cand-stage-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          {!cand.job_id ? (
            <Button onClick={findBestJobs} disabled={matching} className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill" data-testid="match-jobs-btn">
              {matching ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recherche IA…</> : <><Sparkles className="w-4 h-4 mr-2" /> Trouver le meilleur poste</>}
            </Button>
          ) : (
            <Button onClick={analyze} disabled={analyzing} className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill" data-testid="analyze-btn">
              {analyzing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse…</> : <><Sparkles className="w-4 h-4 mr-2" /> {ai.justification ? "Relancer l'analyse" : "Analyser le CV"}</>}
            </Button>
          )}
        </div>
      </div>

      {jobMatches && jobMatches.matches?.length > 0 && (
        <div className="mb-10 border border-blue-200 bg-blue-50/40 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <h3 className="font-display text-lg">Postes suggérés par l'IA</h3>
            {jobMatches.matches_at && <span className="text-xs text-slate-500 font-mono">· analysé le {new Date(cand.ai?.job_matches_at || Date.now()).toLocaleDateString()}</span>}
          </div>
          <div className="space-y-3">
            {jobMatches.matches.slice(0, 3).map((m, i) => (
              <div key={i} className="bg-white border border-slate-200 p-4 flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-xs text-slate-400">#{i + 1}</span>
                    <span className="font-medium">{m.job_title}</span>
                    <Badge className="bg-slate-900 text-white hover:bg-slate-900 font-mono text-[10px]">{m.verdict}</Badge>
                    {cand.job_id === m.job_id && <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-mono text-[10px]">Poste actuel</Badge>}
                  </div>
                  <div className="text-xs text-slate-600 mb-2">{m.summary}</div>
                  {m.matched_skills?.map((s, si) => (
                    <div key={si} className="text-xs text-slate-500 mt-1 italic pl-3 border-l-2 border-emerald-300">
                      <CheckCircle2 className="inline w-3 h-3 text-emerald-600 mr-1" />
                      <span className="text-slate-800 not-italic font-medium">{s.skill}</span> — "{s.evidence}"
                    </div>
                  ))}
                  {m.missing_skills?.length > 0 && (
                    <div className="text-xs text-slate-500 mt-2">
                      <strong>Manquant :</strong> {m.missing_skills.join(", ")}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <div className="font-mono text-2xl font-bold tabular-nums">{m.score}<span className="text-xs text-slate-400">/100</span></div>
                  {cand.job_id !== m.job_id && (
                    <Button onClick={() => assignJob(m.job_id)} size="sm" className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill" data-testid={`assign-job-${i}`}>
                      Assigner
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {professionSuggestions && professionSuggestions.suggestions?.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h3 className="font-display text-2xl tracking-tight">Recommandation IA</h3>
            {cand.ai?.profession_suggestions_at && <span className="text-xs text-slate-500 font-mono">· analysé le {new Date(cand.ai.profession_suggestions_at).toLocaleDateString()}</span>}
          </div>

          {/* MÉTIER PRINCIPAL RECOMMANDÉ */}
          {(() => {
            const top = professionSuggestions.suggestions[0];
            return (
              <div className="relative border-2 border-purple-500 bg-gradient-to-br from-purple-50 to-white p-6 mb-6" data-testid="prof-top-recommendation">
                <div className="absolute -top-3 left-6 bg-purple-600 text-white text-[10px] tracking-widest uppercase px-3 py-1 font-mono">
                  ⭐ Métier recommandé pour ce candidat
                </div>
                <div className="flex items-start justify-between gap-4 flex-wrap mt-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="font-display text-3xl">{top.profession_title}</span>
                      {top.category && <Badge className="bg-purple-100 text-purple-800 border border-purple-200 hover:bg-purple-100 font-mono text-xs">{top.category}</Badge>}
                      <Badge className="bg-slate-900 text-white hover:bg-slate-900 font-mono text-xs">{top.verdict}</Badge>
                    </div>
                    <p className="text-slate-700 text-base mt-3">{top.summary}</p>
                    {top.must_have_list?.length > 0 && (
                      <div className="mt-3 text-sm text-slate-600">
                        <strong>Exigences officielles :</strong> {top.must_have_list.join(", ")}
                      </div>
                    )}
                    {top.matched_requirements?.length > 0 && (
                      <div className="mt-4">
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Preuves depuis le CV</div>
                        <div className="space-y-1.5">
                          {top.matched_requirements.map((mr, mri) => (
                            <div key={mri} className={`text-sm pl-3 border-l-2 ${mr.passed ? "border-emerald-500" : "border-red-400"}`}>
                              {mr.passed ? <CheckCircle2 className="inline w-4 h-4 text-emerald-600 mr-1" /> : <XCircle className="inline w-4 h-4 text-red-600 mr-1" />}
                              <span className="font-medium">{mr.requirement}</span>
                              <div className="italic text-slate-600 mt-0.5 text-xs">"{mr.evidence}"</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {top.missing_requirements?.length > 0 && (
                      <div className="mt-3 text-xs text-slate-600">
                        <strong>À investiguer en entretien :</strong> {top.missing_requirements.join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-3 shrink-0">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 text-right">Score</div>
                      <div className="font-mono text-6xl font-bold tabular-nums text-purple-700">{top.score}<span className="text-2xl text-slate-400">/100</span></div>
                    </div>
                    {!cand.job_id && (
                      <Button
                        onClick={() => createJobFromProfession(top.profession_title)}
                        disabled={creatingJob === top.profession_title}
                        className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill h-11 px-6 whitespace-nowrap"
                        data-testid="create-top-job"
                      >
                        {creatingJob === top.profession_title ? "Création…" : "✓ Créer & assigner ce poste"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ALTERNATIVES (moins prominente) */}
          {professionSuggestions.suggestions.length > 1 && (
            <details className="border border-slate-200 bg-slate-50/50 p-4" open={false}>
              <summary className="cursor-pointer text-sm font-medium text-slate-700 hover:text-slate-900 flex items-center gap-2">
                <span>Voir les {professionSuggestions.suggestions.length - 1} autres métiers analysés</span>
                <span className="text-xs text-slate-400 font-mono">(scores {professionSuggestions.suggestions[1]?.score}–{professionSuggestions.suggestions[professionSuggestions.suggestions.length - 1]?.score})</span>
              </summary>
              <div className="mt-4 space-y-2">
                {professionSuggestions.suggestions.slice(1).map((s, i) => (
                  <div key={i} className="bg-white border border-slate-200 p-3 flex items-start justify-between gap-3" data-testid={`prof-alternative-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-slate-400">#{i + 2}</span>
                        <span className="font-medium">{s.profession_title}</span>
                        {s.category && <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 font-mono text-[10px]">{s.category}</Badge>}
                        <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200 font-mono text-[10px]">{s.verdict}</Badge>
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{s.summary}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="font-mono text-xl font-bold tabular-nums text-slate-500">{s.score}<span className="text-xs text-slate-400">/100</span></div>
                      {!cand.job_id && (
                        <Button
                          onClick={() => createJobFromProfession(s.profession_title)}
                          disabled={creatingJob === s.profession_title}
                          size="sm"
                          variant="outline"
                          className="btn-pill text-xs"
                          data-testid={`create-alt-${i}`}
                        >
                          Choisir
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {score != null && (
        <div className="grid md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mb-8">
          <div className="bg-white p-6">
            <div className="text-xs tracking-wider uppercase text-slate-500">Score match</div>
            <div className="font-mono text-5xl font-bold mt-2 tabular-nums" data-testid="cand-score">{score}<span className="text-xl text-slate-400">/100</span></div>
          </div>
          <div className="bg-white p-6">
            <div className="text-xs tracking-wider uppercase text-slate-500">Recommandation</div>
            <div className="font-display text-2xl mt-2 capitalize">{just.recommendation || "—"}</div>
            <div className="text-xs text-slate-500 mt-1">Fit: {just.fit_for_role || "—"}</div>
          </div>
          <div className="bg-white p-6">
            <div className="text-xs tracking-wider uppercase text-slate-500">Barrières</div>
            <div className="font-display text-2xl mt-2 flex items-center gap-2">
              {barrier.barrier_pass ? <><CheckCircle2 className="w-6 h-6 text-emerald-600" /> Passées</> : <><XCircle className="w-6 h-6 text-red-600" /> Échec</>}
            </div>
          </div>
          <div className="bg-white p-6">
            <div className="text-xs tracking-wider uppercase text-slate-500">Risque triage</div>
            <div className="font-display text-2xl mt-2 capitalize">{triage.risk_level || "—"}</div>
            {triage.gaps?.length > 0 && <div className="text-xs text-amber-700 mt-1">{triage.gaps.length} gap(s) détecté(s)</div>}
          </div>
        </div>
      )}

      <Tabs defaultValue="justification" className="w-full">
        <TabsList className="bg-transparent border-b border-slate-200 w-full justify-start rounded-none h-auto p-0 gap-6">
          <TabsTrigger value="justification" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:shadow-none pb-3 px-0" data-testid="tab-justification">Justification</TabsTrigger>
          <TabsTrigger value="profile" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:shadow-none pb-3 px-0" data-testid="tab-profile">Profil normalisé</TabsTrigger>
          <TabsTrigger value="triage" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:shadow-none pb-3 px-0" data-testid="tab-triage">Triage & Barrières</TabsTrigger>
          <TabsTrigger value="interview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:shadow-none pb-3 px-0" data-testid="tab-interview">Plan d'entretien</TabsTrigger>
          <TabsTrigger value="decision" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:shadow-none pb-3 px-0" data-testid="tab-decision">Décision</TabsTrigger>
          <TabsTrigger value="actions" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:shadow-none pb-3 px-0" data-testid="tab-actions">Actions & Message</TabsTrigger>
        </TabsList>

        <TabsContent value="justification" className="pt-8">
          {!just.short_summary ? <EmptyState text="Lancez l'analyse IA pour générer la justification traçable." onAction={cand.job_id ? analyze : null} actionLoading={analyzing} /> : (
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-display text-xl mb-3">Résumé</h3>
                <p className="text-slate-700 leading-relaxed">{just.short_summary}</p>
                <h3 className="font-display text-xl mb-3 mt-8">Points forts</h3>
                <div className="space-y-3">
                  {(just.strengths || []).map((s, i) => (
                    <div key={i} className="border border-slate-200 p-4 bg-white" data-testid={`strength-${i}`}>
                      <div className="flex items-start gap-2 font-medium"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{s.point}</div>
                      <div className="text-xs text-slate-500 mt-2 italic font-mono">"{s.evidence}"</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-display text-xl mb-3">Réserves</h3>
                <div className="space-y-3">
                  {(just.concerns || []).map((s, i) => (
                    <div key={i} className="border border-slate-200 p-4 bg-white" data-testid={`concern-${i}`}>
                      <div className="flex items-start gap-2 font-medium"><AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />{s.point}</div>
                      <div className="text-xs text-slate-500 mt-2 italic font-mono">"{s.evidence}"</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="profile" className="pt-8 space-y-6">
          {!norm.full_name ? <EmptyState text="Lancez l'analyse pour normaliser ce profil." /> : (
            <>
              <div>
                <div className="text-xs tracking-wider uppercase text-slate-500 mb-1">Titre actuel</div>
                <div className="font-display text-2xl">{norm.title_current}</div>
                <p className="text-slate-600 mt-2">{norm.summary}</p>
                <div className="text-sm mt-2 font-mono text-slate-500">{norm.total_years_experience} an(s) d'expérience · {norm.location || "—"}</div>
              </div>
              <div>
                <div className="text-xs tracking-wider uppercase text-slate-500 mb-2">Compétences</div>
                <div className="grid md:grid-cols-2 gap-3">
                  {(norm.skills || []).map((s, i) => (
                    <div key={i} className="border border-slate-200 p-3 bg-white flex items-start gap-3">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{s.name}</div>
                        <div className="text-xs text-slate-500 italic mt-1 font-mono">"{s.evidence}"</div>
                      </div>
                      <Badge className="bg-slate-900 text-white hover:bg-slate-900 font-mono text-[10px]">{s.level}</Badge>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs tracking-wider uppercase text-slate-500 mb-2">Expériences</div>
                <div className="space-y-2">
                  {(norm.experiences || []).map((e, i) => (
                    <div key={i} className="border-l-2 border-slate-300 pl-4 py-2">
                      <div className="font-medium">{e.title} · <span className="text-slate-600">{e.company}</span></div>
                      <div className="text-xs text-slate-500 font-mono">{e.start} → {e.end} ({e.duration_months} mois)</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="triage" className="pt-8 space-y-8">
          <div>
            <h3 className="font-display text-xl mb-3">Triage anti-erreurs</h3>
            {!triage.risk_level ? <EmptyState text="Lancez l'analyse." onAction={cand.job_id ? analyze : null} actionLoading={analyzing} /> : (
              <div className="space-y-3">
                <div>Niveau: <Badge className={triage.risk_level === "high" ? "bg-red-100 text-red-700" : triage.risk_level === "medium" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}>{triage.risk_level}</Badge></div>
                {(triage.gaps || []).length > 0 && (
                  <div>
                    <div className="text-xs uppercase text-slate-500 mb-1">Gaps détectés</div>
                    {triage.gaps.map((g, i) => <div key={i} className="text-sm border-l-2 border-amber-400 pl-3 py-1">{g.start} → {g.end} ({g.months} mois) — {g.comment}</div>)}
                  </div>
                )}
                {(triage.inconsistencies || []).length > 0 && (
                  <div>
                    <div className="text-xs uppercase text-slate-500 mb-1">Incohérences</div>
                    {triage.inconsistencies.map((g, i) => <div key={i} className="text-sm border-l-2 border-red-400 pl-3 py-1">{g.type}: {g.detail}</div>)}
                  </div>
                )}
                {triage.title_mismatch?.present && <div className="text-sm border-l-2 border-amber-400 pl-3 py-1">{triage.title_mismatch.detail}</div>}
                <p className="text-slate-600 mt-2 text-sm">{triage.overall_comment}</p>
              </div>
            )}
          </div>
          <div>
            <h3 className="font-display text-xl mb-3">Matching barrières (must-have)</h3>
            {!barrier.checks ? <EmptyState text="Lancez l'analyse." onAction={cand.job_id ? analyze : null} actionLoading={analyzing} /> : (
              <div className="space-y-2">
                {barrier.checks.map((c, i) => (
                  <div key={i} className="border border-slate-200 p-4 bg-white">
                    <div className="flex items-start gap-2 font-medium">
                      {c.passed ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />}
                      {c.criterion}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 italic font-mono">{c.evidence}</div>
                    <div className="text-xs text-slate-600 mt-1">{c.reason}</div>
                  </div>
                ))}
                <p className="text-sm text-slate-600 mt-2">{barrier.global_reason}</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="interview" className="pt-8">
          {!plan ? (
            <div className="text-center py-12">
              <p className="text-slate-500 mb-4">Aucun plan d'entretien généré.</p>
              <Button onClick={genPlan} disabled={generatingPlan} className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill" data-testid="gen-plan-btn">
                {generatingPlan ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération…</> : <><CalIcon className="w-4 h-4 mr-2" /> Générer le plan d'entretien</>}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => copy(JSON.stringify(plan, null, 2))} className="btn-pill" data-testid="copy-plan-btn"><Copy className="w-4 h-4 mr-2" /> Copier le pack</Button>
              </div>
              <div>
                <h3 className="font-display text-xl mb-2">Objectifs</h3>
                <ul className="list-disc pl-6 text-slate-700 space-y-1">{(plan.objectives || []).map((o, i) => <li key={i}>{o}</li>)}</ul>
              </div>
              <div>
                <h3 className="font-display text-xl mb-2">Agenda</h3>
                <div className="space-y-1">
                  {(plan.agenda || []).map((a, i) => <div key={i} className="flex justify-between border-b border-slate-100 py-2"><span>{a.step}</span><span className="font-mono text-sm text-slate-500">{a.duration_min} min</span></div>)}
                </div>
              </div>
              <div>
                <h3 className="font-display text-xl mb-2">Questions</h3>
                <div className="space-y-3">
                  {(plan.questions || []).map((q, i) => (
                    <div key={i} className="border border-slate-200 p-4 bg-white">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-slate-900 text-white hover:bg-slate-900 font-mono text-[10px]">{q.category}</Badge>
                      </div>
                      <div className="font-medium">{q.question}</div>
                      {q.follow_up?.length > 0 && <div className="mt-2 text-sm text-slate-600"><span className="text-xs uppercase tracking-wider">Relances:</span> {q.follow_up.join(" / ")}</div>}
                      <div className="mt-1 text-xs text-slate-500 italic">À chercher : {q.what_to_look_for}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-display text-xl mb-2">Grille de scoring</h3>
                <div className="space-y-2">
                  {(plan.scoring_rubric || []).map((r, i) => (
                    <div key={i} className="border border-slate-200 p-3 bg-white">
                      <div className="flex items-center justify-between">
                        <div className="font-medium">{r.criterion}</div>
                        <div className="text-xs font-mono text-slate-500">poids {r.weight}</div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-2 text-xs text-slate-600">
                        <div><span className="font-mono">1·</span> {r.levels?.["1"]}</div>
                        <div><span className="font-mono">3·</span> {r.levels?.["3"]}</div>
                        <div><span className="font-mono">5·</span> {r.levels?.["5"]}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {plan.red_flags_to_probe?.length > 0 && (
                <div>
                  <h3 className="font-display text-xl mb-2">Points à investiguer</h3>
                  <ul className="list-disc pl-6 text-slate-700">{plan.red_flags_to_probe.map((r, i) => <li key={i}>{r}</li>)}</ul>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="decision" className="pt-8">
          {!decision ? (
            <div className="text-center py-12">
              <p className="text-slate-500 mb-4">Aucune décision générée.</p>
              <Button onClick={genDecision} disabled={generatingDecision || !just.match_score} className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill" data-testid="gen-decision-btn">
                {generatingDecision ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération…</> : <><Brain className="w-4 h-4 mr-2" /> Lancer le copilote</>}
              </Button>
              {!just.match_score && <div className="text-xs text-slate-400 mt-2">(Analysez le CV d'abord)</div>}
            </div>
          ) : (
            <div className="space-y-6 max-w-3xl">
              <div className="border border-slate-200 p-6 bg-white">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Recommandation</div>
                    <div className="font-display text-4xl mt-2 capitalize" data-testid="decision-value">{decision.decision}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-wider text-slate-500">Confiance</div>
                    <div className="font-mono text-3xl font-bold">{decision.confidence}<span className="text-base text-slate-400">%</span></div>
                  </div>
                </div>
                <p className="text-slate-700 mt-4 leading-relaxed">{decision.rationale}</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display text-xl">Note interne</h3>
                  <Button variant="outline" size="sm" onClick={() => copy(decision.internal_note)} className="btn-pill" data-testid="copy-note-btn"><Copy className="w-3 h-3 mr-2" /> Copier</Button>
                </div>
                <pre className="border border-slate-200 p-4 bg-slate-50 whitespace-pre-wrap text-sm font-mono">{decision.internal_note}</pre>
              </div>
              {decision.next_actions?.length > 0 && (
                <div>
                  <h3 className="font-display text-xl mb-2">Prochaines actions suggérées</h3>
                  <ul className="list-disc pl-6 text-slate-700">{decision.next_actions.map((a, i) => <li key={i}>{a}</li>)}</ul>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="actions" className="pt-8 space-y-8">
          <div className="flex flex-wrap gap-3">
            <Dialog open={genMsgOpen} onOpenChange={setGenMsgOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0F172A] hover:bg-[#1E293B] btn-pill" data-testid="open-msg-btn"><Mail className="w-4 h-4 mr-2" /> Rédiger un message</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Message au candidat</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <Select value={msgStage} onValueChange={setMsgStage}>
                    <SelectTrigger data-testid="msg-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="screening">Pré-qualification</SelectItem>
                      <SelectItem value="interview_invite">Invitation entretien</SelectItem>
                      <SelectItem value="follow_up">Relance</SelectItem>
                      <SelectItem value="offer">Proposition</SelectItem>
                      <SelectItem value="rejection">Refus</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={genMessage} className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill w-full" data-testid="gen-msg-btn">Générer</Button>
                  {message && (
                    <div className="space-y-3 mt-4">
                      <div>
                        <Label className="text-xs uppercase">Objet</Label>
                        <Input value={message.subject || ""} readOnly className="mt-1.5 font-mono text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase">Corps</Label>
                        <Textarea value={message.body || ""} readOnly className="mt-1.5 min-h-[180px] text-sm font-body" />
                      </div>
                      <Button variant="outline" onClick={() => copy(`Objet: ${message.subject}\n\n${message.body}`)} className="w-full btn-pill"><Copy className="w-4 h-4 mr-2" /> Copier le message</Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={actionOpen} onOpenChange={setActionOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="btn-pill" data-testid="open-action-btn">+ Créer une action</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nouvelle action</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Select value={actionForm.type} onValueChange={(v) => setActionForm({ ...actionForm, type: v })}>
                    <SelectTrigger data-testid="action-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="relance">Relance</SelectItem>
                      <SelectItem value="call">Call</SelectItem>
                      <SelectItem value="test">Test technique</SelectItem>
                      <SelectItem value="interview">Entretien</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input placeholder="Titre" value={actionForm.title} onChange={(e) => setActionForm({ ...actionForm, title: e.target.value })} data-testid="action-title" />
                  <Textarea placeholder="Notes" value={actionForm.notes} onChange={(e) => setActionForm({ ...actionForm, notes: e.target.value })} data-testid="action-notes" />
                  <Input type="datetime-local" value={actionForm.due_at} onChange={(e) => setActionForm({ ...actionForm, due_at: e.target.value })} data-testid="action-due" />
                  <Button onClick={createAction} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill" data-testid="action-submit">Créer</Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={pmOpen} onOpenChange={setPmOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="btn-pill" data-testid="open-pm-btn">+ Post-mortem</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Post-mortem du recrutement</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Select value={pmForm.outcome} onValueChange={(v) => setPmForm({ ...pmForm, outcome: v })}>
                    <SelectTrigger data-testid="pm-outcome"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hired_success">Recruté · succès</SelectItem>
                      <SelectItem value="hired_failure">Recruté · échec</SelectItem>
                      <SelectItem value="rejected_good">Refusé · bonne décision</SelectItem>
                      <SelectItem value="rejected_bad">Refusé · mauvaise décision</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="number" placeholder="Mois dans le poste" value={pmForm.months_in_role} onChange={(e) => setPmForm({ ...pmForm, months_in_role: e.target.value })} data-testid="pm-months" />
                  <Textarea placeholder="Apprentissages, signaux manqués, etc." value={pmForm.notes} onChange={(e) => setPmForm({ ...pmForm, notes: e.target.value })} className="min-h-[120px]" data-testid="pm-notes" />
                  <Button onClick={createPostMortem} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill" data-testid="pm-submit">Enregistrer</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="border border-slate-200 p-6 bg-slate-50">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">CV brut</div>
            <pre className="text-xs font-mono whitespace-pre-wrap max-h-72 overflow-auto text-slate-700">{cand.cv_text?.slice(0, 4000)}{cand.cv_text?.length > 4000 ? "…" : ""}</pre>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EmptyState({ text, onAction, actionLabel, actionLoading }) {
  return (
    <div className="text-center py-12 text-slate-500">
      <FileText className="w-8 h-8 mx-auto mb-3 text-slate-300" />
      <p className="mb-4">{text}</p>
      {onAction && (
        <Button onClick={onAction} disabled={actionLoading} className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill" data-testid="empty-state-action">
          {actionLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse…</> : <><Sparkles className="w-4 h-4 mr-2" /> {actionLabel || "Analyser le CV"}</>}
        </Button>
      )}
    </div>
  );
}
